import React, { useMemo } from 'react';

import { Button } from '@components/Button';
import { SpinnerModal } from '@components/SpinnerModal';
import { YesNoInput } from '@components/utilities/MembershipForm/Form';
import { usePatchCompanyChecklist } from '@hooks/mutation/dashboard/usePatchCompanyChecklist';
import { useSearch } from '@hooks/useSearch';
import { ChecklistForm, MembershipFormFields } from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { Prompt } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    save: '保存',
    defaultTransactionConditions: '普通',
    proposedTransactionConditions: '相談したい',
    question1: '1、リアルタイム口座をお持ちですか？',
    question2: '2、延納口座をお持ちですか？',
    question3: '3、輸出入者符号を取得されていますか？',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
});

interface CompanyInfoFormProps {
  initialValues: ChecklistForm;
  onFinish: () => void;
}

export const ChecklistFormik: React.FC<CompanyInfoFormProps> = (props) => {
  const { initialValues, onFinish } = props;

  const schema = useMemo(() => MembershipFormFields.shared.toChecklistShortFormSchema(), []);
  const validate = useMemo(() => createValidate(schema), [schema]);

  const { search } = useSearch(searchType);

  const patchChecklist = usePatchCompanyChecklist(search.id);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      validateOnMount
      onSubmit={async (values) => {
        const validation = schema.decode(values);

        if (!isRight(validation)) {
          throw new Error('CompanyFormik: Unexpected erroneus form values. ');
        }

        try {
          await patchChecklist.mutateAsync(validation.right);
          onFinish();
        } catch (e) {
          if (e instanceof FormError) {
            // eslint-disable-next-line no-alert
            alert(e.getDisplayMessage());
          }
          throw e;
        }
      }}
    >
      {({ touched, isSubmitting }) => (
        <Form className={clsx(classes.formContainer, classes.verticalSpace1)}>
          <Prompt
            when={Object.keys(touched).length > 0 && !isSubmitting}
            message={() => formStrings.promptLeaving}
          />

          <SpinnerModal open={patchChecklist.isLoading} />

          <YesNoInput
            field={{ ...MembershipFormFields.shared.question2, label: strings.question1 }}
          />
          <YesNoInput
            field={{ ...MembershipFormFields.shared.question3, label: strings.question2 }}
          />
          <YesNoInput
            field={{ ...MembershipFormFields.shared.question6, label: strings.question3 }}
          />
          <Button
            type="submit"
            color="primary"
            className={classes.formButton}
            text={strings.save}
            shape="round"
          />
        </Form>
      )}
    </Formik>
  );
};
